/**
 * CAL Matrix Editor Component
 *
 * Interactive 4x4 grid editor for CAL Matrix (Impact × Attack Vector → CAL1-CAL4)
 */

import React from 'react';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import InstructionPanel from '../../../components/ui/InstructionPanel/InstructionPanel';
import { getCALColor } from '../../../utils/severityStyle';
import styles from './MatrixEditor.module.css';

const IMPACT_LEVELS = ['Severe', 'Major', 'Moderate', 'Negligible'];
const ATTACK_VECTORS = ['Network', 'Adjacent', 'Local', 'Physical'];

export default function CALMatrixEditor() {
  const { config, updateConfiguration } = useConfiguration();

  if (!config?.configuration?.cal_matrix) {
    return <div className={styles.noData}>No CAL matrix configuration available</div>;
  }

  const calMatrix = config.configuration.cal_matrix;

  const handleCellChange = (impact, attackVector, newValue) => {
    const key = `${impact},${attackVector}`;

    const updatedMatrix = {
      ...calMatrix,
      values: {
        ...calMatrix.values,
        [key]: newValue
      }
    };

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        cal_matrix: updatedMatrix
      }
    };

    updateConfiguration(updatedConfig);
  };

  const getCALValue = (impact, attackVector) => {
    const key = `${impact},${attackVector}`;
    return calMatrix.values[key] || 'CAL1';
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>CAL Matrix Configuration</h2>
      </div>

      <div className={styles.matrixContainer}>
        <div className={styles.matrixWrapper}>
          {/* Matrix grid with Y-axis on left */}
          <div className={styles.matrixContent}>
            {/* Y-axis section (left side) */}
            <div className={styles.yAxisSection}>
              <div className={styles.yAxisLabel}>
                <span>Impact Level</span>
              </div>
              <div className={styles.yAxis}>
                {IMPACT_LEVELS.map((impact) => (
                  <div key={impact} className={styles.axisLabel}>
                    {impact}
                  </div>
                ))}
              </div>
            </div>

            {/* Grid cells */}
            <div className={styles.gridContainer}>
              <div className={styles.grid}>
                {IMPACT_LEVELS.map((impact) => (
                  <div key={impact} className={styles.gridRow}>
                    {ATTACK_VECTORS.map((attackVector) => {
                      const value = getCALValue(impact, attackVector);
                      const color = getCALColor(value);

                      return (
                        <div
                          key={`${impact}-${attackVector}`}
                          className={styles.gridCell}
                          style={{ backgroundColor: color, borderColor: color }}
                        >
                          <select
                            value={value}
                            onChange={(e) => handleCellChange(impact, attackVector, e.target.value)}
                            className={styles.cellSelect}
                          >
                            <option value="---">--- (No CAL Required)</option>
                            <option value="CAL1">CAL1 (Basic)</option>
                            <option value="CAL2">CAL2 (Enhanced)</option>
                            <option value="CAL3">CAL3 (High)</option>
                            <option value="CAL4">CAL4 (Maximum)</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* X-axis labels */}
              <div className={styles.xAxis}>
                {ATTACK_VECTORS.map((attackVector) => (
                  <div key={attackVector} className={styles.axisLabel}>
                    {attackVector}
                  </div>
                ))}
              </div>

              {/* X-axis label */}
              <div className={styles.xAxisLabel}>
                <span>Attack Vector</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        <h3>CAL Level Legend</h3>
        <div className={styles.legendItems}>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getCALColor('CAL4') }}></div>
            <span>CAL4 - Maximum Assurance (Defense in depth, formal verification)</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getCALColor('CAL3') }}></div>
            <span>CAL3 - High Assurance (HSM, end-to-end encryption)</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getCALColor('CAL2') }}></div>
            <span>CAL2 - Enhanced Assurance (Secure boot, intrusion detection)</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getCALColor('CAL1') }}></div>
            <span>CAL1 - Basic Assurance (Standard authentication, logging)</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getCALColor('---') }}></div>
            <span>--- - No CAL Required (Negligible impact scenarios)</span>
          </div>
        </div>
      </div>

      {/* Help text */}
      <InstructionPanel title="How to use">
        <ul>
          <li>Each cell represents a combination of Impact (vertical) and Attack Vector (horizontal)</li>
          <li><strong>CAL Options:</strong> --- (No CAL), CAL1 (Basic), CAL2 (Enhanced), CAL3 (High), CAL4 (Maximum)</li>
          <li><strong>Attack Vectors:</strong> Network (remote), Adjacent (network access), Local (local access), Physical (physical access)</li>
          <li>Higher impact + easier access (Network) should typically result in higher CAL levels</li>
          <li><strong>ISO 21434 defaults:</strong> Severe+Network=CAL4, Major+Network=CAL4, Negligible=(any)=---, etc.</li>
          <li>Use --- for negligible impact scenarios that require no cybersecurity assurance</li>
          <li>CAL levels determine the rigor of cybersecurity requirements and testing needed</li>
          <li>Changes are saved when you click "Save Changes" in the header</li>
        </ul>
      </InstructionPanel>
    </div>
  );
}
